import { useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import ReCaptcha from 'react-google-recaptcha'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { emailSchema } from '@/types/schemas.ts'
import { CAPTCHA_SITE_KEY } from '@/utils/constants'

import { useAccount } from '../hooks/useAccounts'

export type RegisterFormValues = z.infer<typeof registerFormSchema>

const registerFormSchema = z
  .object({
    email: emailSchema,
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z
      .string()
      .min(1, { message: 'Confirm Password is required' }),
    'g-recaptcha-response': z.string().nonempty(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: "Passwords don't match",
  })

interface Props {
  token: string
  emailToken: string | null
  userMail: string
}

export const RegisterAfterPaymentForm = ({
  token,
  emailToken,
  userMail,
}: Props) => {
  const { registerAfterPayment, setAsLoggedIn } = useAccount()

  const form = useForm<RegisterFormValues>({
    mode: 'onChange',
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      email: userMail,
      password: '',
      confirmPassword: '',
      'g-recaptcha-response': '',
    },
  })

  const { handleSubmit } = form

  const onSubmit = useCallback(
    async (values: RegisterFormValues) => {
      await registerAfterPayment({
        'g-recaptcha-response': values['g-recaptcha-response'],
        token: token,
        emailToken: emailToken || '',
        newPassword: values.password,
      })
      setAsLoggedIn()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailToken, registerAfterPayment, token, userMail],
  )

  return (
    <div>
      <FormProvider {...form}>
        <form
          className="grid gap-8"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="grid gap-4">
            <FormField
              name="email"
              label="Email Address"
              placeholder="mail@example.com"
              disabled
            />

            <FormField
              name="password"
              label="Password"
              placeholder="Password (at least 8 character)"
              type="password"
              isPassword
            />

            <FormField
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Password (at least 8 character)"
              type="password"
              isPassword
            />

            <Controller
              control={form.control}
              name="g-recaptcha-response"
              render={({ field: { onChange } }) => (
                <ReCaptcha
                  sitekey={CAPTCHA_SITE_KEY}
                  onChange={(value) => {
                    if (!value) {
                      return
                    }

                    onChange(value)
                  }}
                />
              )}
            />
          </div>

          <Button
            disabled={!form.formState.isDirty || !form.formState.isValid}
            className="w-full"
            isLoading={form.formState.isSubmitting}
          >
            Continue
          </Button>
        </form>
      </FormProvider>
    </div>
  )
}
